import type { SupportedClient } from "@contracts/contract-2-plugin-store";

export const CLIENT_CONFIG: Record<SupportedClient, { label: string; color: string; bg: string; border: string }> = {
  "claude-chat":   { label: "Claude Chat",    color: "#0e7490", bg: "#ecfeff", border: "#67e8f9" },
  "claude-cowork": { label: "Claude Cowork",  color: "#047857", bg: "#d1fae5", border: "#6ee7b7" },
  "claude-code":   { label: "Claude Code",    color: "#7c3aed", bg: "#f3e8ff", border: "#c4b5fd" },
  "copilot":       { label: "GitHub Copilot", color: "#c2410c", bg: "#fff7ed", border: "#fdba74" },
};

interface ClientBadgeProps {
  client: SupportedClient;
  onClick?: (client: SupportedClient) => void;
}

export default function ClientBadge({ client, onClick }: ClientBadgeProps): JSX.Element {
  const config = CLIENT_CONFIG[client];
  if (!config) {
    console.warn(`[ClientBadge] Unknown client value: "${client}". Entry may predate the client rename. Run the reprocess script to migrate.`);
  }
  const resolved = config ?? { label: client, color: "#666", bg: "#f0f0f0", border: "#ccc" };

  const sharedStyle = {
    display: "inline-block",
    fontSize: "0.75rem",
    fontWeight: 600 as const,
    color: resolved.color,
    backgroundColor: resolved.bg,
    padding: "0.2rem 0.5rem",
    borderRadius: "9999px",
    whiteSpace: "nowrap" as const,
  };

  if (onClick) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClick(client); }}
        style={{ ...sharedStyle, cursor: "pointer", border: "none", fontFamily: "inherit" }}
      >
        {resolved.label}
      </button>
    );
  }

  return (
    <span style={{ ...sharedStyle, cursor: "default" }}>
      {resolved.label}
    </span>
  );
}

interface ClientBadgeListProps {
  clients: SupportedClient[];
  onClientClick?: (client: SupportedClient) => void;
}

export function ClientBadgeList({ clients, onClientClick }: ClientBadgeListProps): JSX.Element | null {
  if (!clients || clients.length === 0) return null;

  return (
    <div style={{ display: "inline-flex", flexWrap: "wrap", gap: "0.25rem" }}>
      {clients.map((client) => (
        <ClientBadge key={client} client={client} onClick={onClientClick} />
      ))}
    </div>
  );
}
